import React, { useState, useRef, useCallback } from "react";

/**
 * Lounge-doctrine hover/focus popup for short governance explanations.
 *
 * Defaults render the IAMA explainer (legacy default — kept stable so existing
 * call-sites keep working), but you can override `title` and `body` to reuse
 * the exact same visual chrome for any other juridical term — e.g. FG/DPO.
 *
 * Usage:
 *   <IamaTooltip>
 *     <span>IAMA verplicht</span>
 *   </IamaTooltip>
 *
 *   <IamaTooltip
 *     title="Functionaris Gegevensbescherming (FG/DPO)"
 *     body="De inzet van een FG is wettelijk verplicht voor overheden …"
 *   >
 *     <span>Hoog-risico · FG/DPO verplicht</span>
 *   </IamaTooltip>
 */
const DEFAULT_TITLE = "Impact Assessment Mensenrechten (IAMA)";
const DEFAULT_BODY =
  "De IAMA is een verplicht instrument voor de Nederlandse overheid om de " +
  "risico's van een algoritme op fundamentele rechten — zoals privacy, " +
  "non-discriminatie en de menselijke waardigheid — vooraf in kaart te " +
  "brengen. Voor hoog-risico systemen onder de EU AI Act is een dergelijke " +
  "toetsing juridisch onvermijdbaar.";

const IamaTooltip = ({
  children,
  placement = "top",
  title = DEFAULT_TITLE,
  body = DEFAULT_BODY,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => setOpen(false), []);

  const isTop = placement === "top";

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      data-testid="iama-tooltip-wrapper"
    >
      <span
        tabIndex={0}
        className="inline-flex outline-none"
        aria-describedby="iama-tooltip-content"
      >
        {children}
      </span>

      {open && (
        <span
          id="iama-tooltip-content"
          role="tooltip"
          data-testid="iama-tooltip-popup"
          className="absolute z-50 pointer-events-none"
          style={{
            width: 320,
            left: "50%",
            transform: "translateX(-50%)",
            [isTop ? "bottom" : "top"]: "calc(100% + 10px)",
          }}
        >
          <span
            className="block text-left"
            style={{
              background: "#0F1E33",
              border: "1px solid rgba(127,215,228,0.35)",
              boxShadow:
                "0 24px 60px -20px rgba(0,0,0,0.65), 0 0 0 1px rgba(192,197,206,0.06) inset",
              padding: "14px 16px",
              fontFamily: "Outfit, system-ui, sans-serif",
            }}
          >
            <span
              className="block mb-2"
              style={{
                color: "#7FD7E4",
                fontSize: 10,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {title}
            </span>
            <span
              className="block"
              style={{
                color: "#D4D8DF",
                fontSize: 12.5,
                lineHeight: 1.55,
                fontWeight: 400,
              }}
            >
              {body}
            </span>
          </span>
          {/* Caret */}
          <span
            aria-hidden
            className="absolute"
            style={{
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 10,
              height: 10,
              background: "#0F1E33",
              borderRight: "1px solid rgba(127,215,228,0.35)",
              borderBottom: "1px solid rgba(127,215,228,0.35)",
              [isTop ? "bottom" : "top"]: -6,
            }}
          />
        </span>
      )}
    </span>
  );
};

export default IamaTooltip;
